/**
 * Role-aware curated welcome prompts shown on the Kai new-chat screen.
 *
 * These are the always-available baseline: they render instantly with no
 * network call and act as the fallback when the `kai_welcome_prompts` feature
 * flag is off, or when the personalized (LLM-generated) fetch fails. Savings
 * and rebook prompts are filtered out for callers who cannot view savings.
 */

export interface KaiWelcomePrompt {
  id: string;
  text: string;
}

export interface WelcomePromptContext {
  role: string | null;
  canViewSavings: boolean;
  isAdmin: boolean;
  isSalesPerson: boolean;
  isClient?: boolean;
}

const CLIENT_PROMPTS: WelcomePromptDef[] = [
  { id: "client-status", text: "Summarize my active shipments by status" },
  { id: "client-departures", text: "Which of my shipments depart this week?" },
  {
    id: "client-cutoffs",
    text: "What's approaching cutoff for my shipments?",
  },
  { id: "client-sailings", text: "Show sailings on my main lanes" },
];

/** A savings-flagged prompt is only shown when the caller can view savings. */
interface WelcomePromptDef extends KaiWelcomePrompt {
  requiresSavings?: boolean;
}

const SALES_PERSON_PROMPTS: WelcomePromptDef[] = [
  { id: "sp-departures", text: "Which of my bookings depart this week?" },
  { id: "sp-by-status", text: "Summarize my active shipments by status" },
  { id: "sp-pending-book", text: "Show my bookings stuck in Pending Book" },
  { id: "sp-top-consignees", text: "Who are my top consignees this month?" },
];

const ADMIN_PROMPTS: WelcomePromptDef[] = [
  { id: "admin-ops-brief", text: "Give me today's ops brief" },
  { id: "admin-pipeline", text: "How is pipeline health across data sources?" },
  { id: "admin-allocations", text: "Show allocation utilization by contract" },
  {
    id: "admin-rebooks",
    text: "Show top rebook opportunities this week",
    requiresSavings: true,
  },
  { id: "admin-carriers", text: "Chart bookings and carrier share" },
];

const OPS_PROMPTS: WelcomePromptDef[] = [
  { id: "ops-departures", text: "Which bookings depart this week?" },
  {
    id: "ops-cutoffs",
    text: "What's approaching cutoff in the next 48 hours?",
  },
  { id: "ops-rates", text: "Find rates Ningbo → Ashdod, 40HC" },
  {
    id: "ops-rebooks",
    text: "Show top rebook opportunities over $2k savings",
    requiresSavings: true,
  },
  { id: "ops-allocations", text: "Show allocation utilization by contract" },
];

/**
 * Return up to four curated starter prompts tailored to the caller's role,
 * dropping savings/rebook prompts when the caller cannot view savings.
 */
export function getRoleWelcomePrompts(
  ctx: WelcomePromptContext,
): KaiWelcomePrompt[] {
  const source = ctx.isClient
    ? CLIENT_PROMPTS
    : ctx.isSalesPerson
      ? SALES_PERSON_PROMPTS
      : ctx.isAdmin
        ? ADMIN_PROMPTS
        : OPS_PROMPTS;

  return source
    .filter((p) => !p.requiresSavings || ctx.canViewSavings)
    .slice(0, 4)
    .map(({ id, text }) => ({ id, text }));
}
